import React, { useState } from "react";
import { XCircle, RefreshCw, MessageCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { createDepositOrder, redirectToCheckout } from "../../lib/payment_helpers";
import { toast } from "sonner";

/**
 * PaymentFailed — shown after a failed/expired Cashfree payment.
 *
 * Props:
 *   orderId    string
 *   rentalId   string
 *   reason     string   optional error message
 */
export default function PaymentFailed({ orderId, rentalId, reason }) {
  const [retrying, setRetrying] = useState(false);

  async function handleRetry() {
    if (!rentalId) { toast.error("Cannot retry — rental ID missing."); return; }
    setRetrying(true);
    try {
      const order = await createDepositOrder(rentalId, 0);
      sessionStorage.setItem("orello_payment_order", order.order_id);
      sessionStorage.setItem("orello_payment_rental", rentalId);
      await redirectToCheckout(order.session_id, order.cashfree_mode);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restart payment.");
      setRetrying(false);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      {/* Icon */}
      <div className="w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-6">
        <XCircle className="h-10 w-10 text-red-500" />
      </div>

      {/* Headline */}
      <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white mb-2">
        Payment could not be confirmed
      </h1>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm mb-4">
        {reason || "The payment confirmation timed out."}
      </p>

      {/* Safety Assurance Box */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg p-4 max-w-sm mb-6">
        <p className="text-sm text-green-800 dark:text-green-300">
          ✅ <strong>Your account has NOT been charged.</strong>
        </p>
        <p className="text-xs text-green-700 dark:text-green-400 mt-2">
          If an amount was deducted by mistake, a full refund will be issued within 3-4 business days.
        </p>
      </div>

      {/* Order Reference */}
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-8">
        Order ID: <span className="font-mono font-semibold">{orderId}</span>
      </p>

      {/* CTAs */}
      <div className="flex flex-col gap-3 w-full max-w-sm">
        {rentalId && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="flex items-center justify-center gap-2 bg-[#F97316] hover:bg-orange-600 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60"
          >
            {retrying ? (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {retrying ? "Starting new payment…" : "Try payment again"}
          </button>
        )}

        <a
          href="mailto:help@orello.in?subject=Payment+failed"
          className="flex items-center justify-center gap-2 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 font-medium py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/04 transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Contact support
        </a>

        {rentalId ? (
          <Link
            to={`/app/rental/${rentalId}`}
            className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm py-2"
          >
            <ArrowLeft className="h-4 w-4" /> Back to deal
          </Link>
        ) : (
          <Link to="/app" className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm py-2">
            <ArrowLeft className="h-4 w-4" /> Go to dashboard
          </Link>
        )}
      </div>
    </div>
  );
}
